import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { IconChevron } from './Icons.jsx'

const DETENTS = ['peek', 'half', 'full']
const FLING = 0.4 // px/ms — a flick faster than this snaps in its direction
const rubber = (x) => 28 * Math.log1p(x / 28) // resistance past the ends

/** Safe-area insets in px (notch, home indicator), read from CSS env(). */
function useSafeArea() {
  const [s, setS] = useState({ top: 0, bottom: 0 })
  useLayoutEffect(() => {
    const probe = document.createElement('div')
    probe.style.cssText =
      'position:fixed;inset:0 auto auto 0;visibility:hidden;pointer-events:none;' +
      'padding-top:env(safe-area-inset-top);padding-bottom:env(safe-area-inset-bottom)'
    document.body.appendChild(probe)
    const read = () => {
      const cs = getComputedStyle(probe)
      setS({ top: parseFloat(cs.paddingTop) || 0, bottom: parseFloat(cs.paddingBottom) || 0 })
    }
    read()
    window.addEventListener('resize', read)
    return () => {
      window.removeEventListener('resize', read)
      probe.remove()
    }
  }, [])
  return s
}

function useViewportHeight() {
  const [vh, setVh] = useState(() => (typeof window === 'undefined' ? 800 : window.innerHeight))
  useEffect(() => {
    const on = () => setVh(window.innerHeight)
    window.addEventListener('resize', on)
    window.addEventListener('orientationchange', on)
    return () => {
      window.removeEventListener('resize', on)
      window.removeEventListener('orientationchange', on)
    }
  }, [])
  return vh
}

/**
 * The one panel, in two shapes:
 *  - 'sheet' (phones held upright): a bottom sheet with three detents — peek
 *    (just the header), half, full. Drag the header anywhere, fling it, or use
 *    the handle (keyboard: Enter cycles, ↑/↓ step). At full, the body scrolls,
 *    and pulling down from its top hands the gesture back to the sheet.
 *  - 'side' (tablets, desktops, landscape phones): a floating card on the left
 *    that can be tucked away.
 * Same header + body in both, so there is one UI to learn.
 *
 * `onOcclusion({bottom, left})` reports how much map the panel covers; the map
 * uses it as camera padding so whatever it centres lands in the visible part.
 * `floating` (sheet only) rides on top of the sheet — locate button, legend —
 * so it moves with the sheet without any per-frame JS.
 */
export default function Panel({
  mode,
  detent,
  onDetent,
  collapsed = false,
  onCollapse,
  header,
  children,
  floating = null,
  viewKey,
  label,
  t,
  onOcclusion,
}) {
  const vh = useViewportHeight()
  const safe = useSafeArea()
  const rootRef = useRef(null)
  const headerRef = useRef(null)
  const bodyRef = useRef(null)
  const [headerH, setHeaderH] = useState(0)
  const [panelW, setPanelW] = useState(0)
  const ty = useRef(null) // current translateY (sheet)
  const drag = useRef(null)
  const pending = useRef(null) // pointer down, not yet a drag
  const swallowClick = useRef(false)
  const detentRef = useRef(detent)
  detentRef.current = detent

  const sheet = mode === 'sheet'

  // Detent heights (visible px). Full leaves a strip of map; half is ~half.
  const fullH = Math.round(Math.max(320, vh - safe.top - 44))
  const peekH = Math.round(Math.min(fullH, headerH + safe.bottom))
  const halfH = Math.round(Math.min(fullH, Math.max(peekH + 150, vh * 0.52)))
  const heights = { peek: peekH, half: halfH, full: fullH }
  const target = heights[detent] ?? peekH

  // Measure the header (it changes with the view) and the side panel width.
  useLayoutEffect(() => {
    const h = headerRef.current
    const r = rootRef.current
    if (!h || !r) return
    const ro = new ResizeObserver(() => {
      setHeaderH(Math.round(h.getBoundingClientRect().height))
      setPanelW(Math.round(r.getBoundingClientRect().width))
    })
    ro.observe(h)
    ro.observe(r)
    return () => ro.disconnect()
  }, [mode])

  const setY = (y) => {
    ty.current = y
    if (rootRef.current) rootRef.current.style.transform = `translate3d(0, ${y}px, 0)`
    // Let CSS hide the floating controls once the sheet covers most of the map.
    if (rootRef.current) rootRef.current.dataset.high = y < fullH - halfH - 24 ? 'true' : 'false'
  }

  // Settle on the current detent (animated by CSS unless dragging / first paint).
  useLayoutEffect(() => {
    if (!sheet || drag.current) return
    const root = rootRef.current
    if (!root) return
    if (ty.current == null || !headerH) root.classList.add('is-instant')
    setY(fullH - target)
    if (headerH) requestAnimationFrame(() => root.classList.remove('is-instant'))
  }, [sheet, fullH, target, headerH])

  // Report occlusion (the TARGET, so the map can move while the sheet animates).
  // Capped at half: at full there's no map left worth centring into.
  useEffect(() => {
    if (sheet) onOcclusion?.({ bottom: Math.min(target, halfH), left: 0 })
    else {
      const r = rootRef.current
      const left = collapsed || !r ? 0 : Math.round(r.offsetLeft + panelW)
      onOcclusion?.({ bottom: 0, left })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet, target, halfH, collapsed, panelW])

  // New view: start its body at the top.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0
  }, [viewKey])

  // ---- drag mechanics (shared by pointer + touch paths) --------------------
  const begin = (y, t) => {
    drag.current = { y0: y, start: ty.current ?? fullH - target, samples: [{ y, t }] }
    rootRef.current?.classList.add('is-dragging')
  }
  const move = (y, t) => {
    const d = drag.current
    if (!d) return
    const maxY = fullH - peekH
    let next = d.start + (y - d.y0)
    if (next < 0) next = -rubber(-next)
    if (next > maxY) next = maxY + rubber(next - maxY)
    setY(next)
    d.samples.push({ y, t })
    if (d.samples.length > 6) d.samples.shift()
  }
  const end = () => {
    const d = drag.current
    drag.current = null
    rootRef.current?.classList.remove('is-dragging')
    if (!d) return
    const a = d.samples[0]
    const b = d.samples[d.samples.length - 1]
    const v = b.t > a.t ? (b.y - a.y) / (b.t - a.t) : 0 // + = downward
    const cur = ty.current ?? 0
    const points = DETENTS.map((name) => [name, fullH - heights[name]])
    const nearest = points.reduce((p, q) => (Math.abs(q[1] - cur) < Math.abs(p[1] - cur) ? q : p))
    let pick = nearest
    if (Math.abs(v) > FLING) {
      const ahead = points.filter(([, y]) => (v > 0 ? y > cur + 1 : y < cur - 1))
      if (ahead.length) pick = ahead.reduce((p, q) => (Math.abs(q[1] - cur) < Math.abs(p[1] - cur) ? q : p))
    }
    setY(pick[1])
    if (pick[0] !== detentRef.current) onDetent?.(pick[0])
    swallowClick.current = true
    setTimeout(() => (swallowClick.current = false), 0)
  }

  // Pointer path: anywhere on the sheet except form fields and, at full, the
  // scrolling body (touch scroll belongs to the browser there).
  const onPointerDown = (e) => {
    if (!sheet || e.button > 0) return
    const target = e.target
    if (target.closest('input, textarea, select, [contenteditable], [data-no-drag]')) return
    if (detentRef.current === 'full' && bodyRef.current?.contains(target)) return
    pending.current = { id: e.pointerId, x: e.clientX, y: e.clientY, t: e.timeStamp }
  }
  const onPointerMove = (e) => {
    const p = pending.current
    if (p && p.id === e.pointerId && !drag.current) {
      const dx = e.clientX - p.x
      const dy = e.clientY - p.y
      if (Math.hypot(dx, dy) < 6) return
      pending.current = null
      if (Math.abs(dx) > Math.abs(dy)) return // sideways: let chip rails scroll
      rootRef.current?.setPointerCapture?.(e.pointerId)
      begin(p.y, p.t)
    }
    if (drag.current) move(e.clientY, e.timeStamp)
  }
  const onPointerUp = () => {
    pending.current = null
    if (drag.current) end()
  }
  const onClickCapture = (e) => {
    if (swallowClick.current) {
      e.stopPropagation()
      e.preventDefault()
      swallowClick.current = false
    }
  }

  // Touch path for the hand-off: at full, pulling down from the top of the
  // scrolled body drags the sheet instead of rubber-banding the content.
  useEffect(() => {
    const body = bodyRef.current
    if (!sheet || !body) return
    let s = null
    const onStart = (e) => {
      if (detentRef.current !== 'full' || e.touches.length !== 1) return
      s = { x: e.touches[0].clientX, y: e.touches[0].clientY, active: false }
    }
    const onMove = (e) => {
      if (!s) return
      const tch = e.touches[0]
      const dx = tch.clientX - s.x
      const dy = tch.clientY - s.y
      if (!s.active) {
        if (Math.hypot(dx, dy) < 6) return
        if (body.scrollTop <= 0 && dy > 0 && Math.abs(dy) > Math.abs(dx)) {
          s.active = true
          begin(s.y, e.timeStamp)
        } else {
          s = null
          return
        }
      }
      if (e.cancelable) e.preventDefault()
      move(tch.clientY, e.timeStamp)
    }
    const onEnd = () => {
      if (s?.active) end()
      s = null
    }
    body.addEventListener('touchstart', onStart, { passive: true })
    body.addEventListener('touchmove', onMove, { passive: false })
    body.addEventListener('touchend', onEnd)
    body.addEventListener('touchcancel', onEnd)
    return () => {
      body.removeEventListener('touchstart', onStart)
      body.removeEventListener('touchmove', onMove)
      body.removeEventListener('touchend', onEnd)
      body.removeEventListener('touchcancel', onEnd)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet, fullH, peekH, halfH])

  // Handle: Enter/click cycles peek → half → full → peek; arrows step.
  const step = (dir) => {
    const i = DETENTS.indexOf(detentRef.current)
    const j = Math.max(0, Math.min(DETENTS.length - 1, i + dir))
    if (j !== i) onDetent?.(DETENTS[j])
  }
  const onHandleKey = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      step(1)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      step(-1)
    }
  }
  const cycle = () => onDetent?.(DETENTS[(DETENTS.indexOf(detentRef.current) + 1) % DETENTS.length])

  if (!sheet) {
    return (
      <aside ref={rootRef} className={`panel panel--side ${collapsed ? 'is-collapsed' : ''}`} aria-label={label}>
        <div className="panel__card" inert={collapsed ? '' : undefined}>
          <div ref={headerRef} className="panel__header">
            {header}
          </div>
          <div ref={bodyRef} className="panel__body">
            {children}
          </div>
        </div>
        <button
          type="button"
          className="panel__toggle"
          onClick={() => onCollapse?.(!collapsed)}
          aria-expanded={!collapsed}
          aria-label={collapsed ? t('expandPanel') : t('collapsePanel')}
          title={collapsed ? t('expandPanel') : t('collapsePanel')}
        >
          <IconChevron />
        </button>
      </aside>
    )
  }

  return (
    <section
      ref={rootRef}
      className="panel panel--sheet"
      data-detent={detent}
      aria-label={label}
      style={{ height: fullH }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClickCapture={onClickCapture}
    >
      {floating && (
        <div className="panel__floating" data-no-drag>
          {floating}
        </div>
      )}
      <div ref={headerRef} className="panel__header">
        <button
          type="button"
          className="panel__handle"
          aria-label={t('sheetHandle')}
          aria-expanded={detent !== 'peek'}
          onClick={cycle}
          onKeyDown={onHandleKey}
        >
          <span aria-hidden="true" />
        </button>
        {header}
      </div>
      <div ref={bodyRef} className="panel__body" data-scroll={detent === 'full' ? 'true' : 'false'}>
        {children}
      </div>
    </section>
  )
}
